import { array } from 'fp-ts';
import { Stack, Typography } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    PortfolioCentralRateGroup,
    PortfolioEnterpriseWithRateCount,
    PortfolioEnterpriseGroup,
} from '@mews-commander/data-access';
import { getPropertyGroupsPropertiesCount, getRatesCountPerPropertyGroup } from '../utils';
import { PropertyGroupsTable } from './PropertyGroupsTable';
import { PropertiesTable } from './PropertiesTable';
import { AssignPropertiesContainer } from './PropertiesTab/AssignPropertiesContainer';
import { PropertiesTabEmptyState } from './PropertiesTab/PropertiesTabEmptyState';

interface PropertiesTabContentProps {
    rateGroup?: PortfolioCentralRateGroup;
    properties?: Array<PortfolioEnterpriseWithRateCount> | null;
    propertyGroups?: Array<PortfolioEnterpriseGroup> | null;
}

export const PropertiesTabContent = ({ rateGroup, properties, propertyGroups }: PropertiesTabContentProps) => {
    if (!rateGroup) {
        return null;
    }

    const shouldDisplayPropertyGroupsTable =
        rateGroup.assignedEnterpriseGroupIds != null &&
        array.isNonEmpty(rateGroup.assignedEnterpriseGroupIds) &&
        propertyGroups != null;
    const shouldDisplayPropertiesTable =
        rateGroup.assignedEnterpriseIds != null && array.isNonEmpty(rateGroup.assignedEnterpriseIds);
    const shouldDisplayEmptyState = !shouldDisplayPropertyGroupsTable && !shouldDisplayPropertiesTable;

    if (shouldDisplayEmptyState) {
        return <PropertiesTabEmptyState rateGroupId={rateGroup.id} />;
    }

    const propertyGroupsCount = rateGroup.assignedEnterpriseGroupIds?.length ?? 0;
    const propertiesCount = rateGroup.assignedEnterpriseIds?.length ?? 0;
    const propertyGroupsPropertiesCount = getPropertyGroupsPropertiesCount(
        rateGroup.assignedEnterpriseGroupIds,
        propertyGroups
    );
    const totalPropertiesCount = propertiesCount + propertyGroupsPropertiesCount;

    const ratesCountPerPropertyGroup = getRatesCountPerPropertyGroup({
        assignedEnterpriseGroupIds: rateGroup.assignedEnterpriseGroupIds,
        centralRateGroupId: rateGroup.id,
        propertyGroups,
        properties,
    });

    return (
        <>
            <Stack distribution="space-between" itemAlignment="center">
                <Stack spacing="100">
                    <Typography textStyle="titleMediumStrong" as="div">
                        <LocalizedText textKey={TextKey.TotalProperties} />
                    </Typography>
                    <Typography textStyle="titleMedium" as="div">
                        {totalPropertiesCount}
                    </Typography>
                </Stack>
                <AssignPropertiesContainer rateGroupId={rateGroup.id} />
            </Stack>
            {shouldDisplayPropertyGroupsTable ? (
                <PropertyGroupsTable
                    assignedEnterpriseGroupIds={rateGroup.assignedEnterpriseGroupIds}
                    propertyGroups={propertyGroups}
                    propertyGroupsCount={propertyGroupsCount}
                    ratesCountPerPropertyGroup={ratesCountPerPropertyGroup}
                    centralRateGroupId={rateGroup.id}
                />
            ) : null}
            {shouldDisplayPropertiesTable ? (
                <PropertiesTable
                    assignedEnterpriseIds={rateGroup.assignedEnterpriseIds}
                    centralRateGroupId={rateGroup.id}
                    propertiesList={properties}
                />
            ) : null}
        </>
    );
};
